import React from 'react'

type ComponentModule<T> = { default: T }
type ImportFactory<T> = () => Promise<ComponentModule<T>>

type RetryOptions = {
  retries?: number
  interval?: number
}

// Guards the automatic reload so a permanently broken chunk can never trap the user in an
// endless reload loop: at most one auto-reload per tab session (survives the reload via
// sessionStorage). Deliberately never cleared on success — see the note in the catch block.
const RELOAD_FLAG = 'smarttips.chunkReloadAttempted'

// If the browser fails to actually reload (blocked by an extension, a test harness, or a
// no-op), fall back to the error boundary after this delay instead of hanging in Suspense.
const RELOAD_SAFETY_TIMEOUT = 10000

const wait = async (ms: number) => await new Promise(resolve => setTimeout(resolve, ms))

// sessionStorage access can throw (Safari private mode, storage disabled, sandboxed iframes).
// Reading defaults to "already reloaded" so a failure can never trigger an unguarded reload loop.
const hasReloaded = () => {
  try {
    return window.sessionStorage.getItem(RELOAD_FLAG) !== null
  } catch {
    return true
  }
}

const markReloaded = () => {
  try {
    window.sessionStorage.setItem(RELOAD_FLAG, '1')
  } catch {
    // Storage unavailable — nothing we can do; the reload simply won't be guarded.
  }
}

async function importWithRetry<T> (factory: ImportFactory<T>, retries: number, interval: number): Promise<ComponentModule<T>> {
  let lastError: unknown
  for (let attempt = 0; attempt <= retries; attempt++) {
    if (attempt > 0) {
      await wait(interval)
    }
    try {
      return await factory()
    } catch (error) {
      lastError = error
    }
  }
  throw lastError
}

/**
 * Drop-in replacement for `React.lazy` that hardens dynamic chunk loading:
 *  - retries the dynamic import a few times with a short delay (transient network hiccups),
 *  - on definitive failure triggers a single automatic `location.reload()` to recover from a
 *    stale build hash left over after a deploy,
 *  - if a chunk still fails to load after that reload, it rethrows so a router `errorElement`
 *    can render a friendly fallback (and report the error) instead of looping forever.
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function lazyWithRetry<T extends React.ComponentType<any>> (
  factory: ImportFactory<T>,
  { retries = 2, interval = 500 }: RetryOptions = {}
): React.LazyExoticComponent<T> {
  return React.lazy(async () => {
    try {
      return await importWithRetry(factory, retries, interval)
    } catch (error) {
      // All retries failed. Try a single automatic reload to recover from a stale build hash
      // after a deploy. The guard is intentionally NOT cleared when some other chunk loads
      // successfully: a page pulls in several chunks in sequence, so clearing it on a sibling's
      // success would reset the guard before the broken chunk is reached — reloading forever.
      if (!hasReloaded()) {
        markReloaded()
        window.location.reload()
        // Keep the Suspense fallback visible while the page reloads instead of briefly flashing
        // the error boundary. The timeout is a safety net: if the reload never happens, surface
        // the fallback rather than hang forever.
        return await new Promise<never>((_resolve, reject) => {
          setTimeout(() => reject(error), RELOAD_SAFETY_TIMEOUT)
        })
      }

      throw error
    }
  })
}
